import { useState, useMemo, useEffect } from "react";

import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DownloadOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  LinkedinOutlined,
  LinkOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { App } from "antd";
import { Helmet } from "react-helmet-async";
import _ from "lodash";
import { DateTime } from "luxon";

import {
  useDeleteSurveyViewMutation,
  useGetSurveyViewByIdQuery,
  useLazyGetSurveyByIdQuery,
  useLazyIncrementSurveyViewViewsQuery,
  useViewSurveyViewQuery,
} from "@/redux/service";
import BaseButton from "@/components_v2/Button/BaseButton";
import { useSurveyViewDownload } from "@/domains/survey-views/hooks/useSurveyViewDownload";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import SurveyIcon from "@/assets/icons/survey-icon.svg?react";
import CommunityIcon from "@/assets/icons/community.svg?react";
import CalendarIcon from "@/assets/icons/calendar.svg?react";
import BaseDivider from "@/components_v2/Divider/BaseDivider";
import DropdownMenu from "@/components_v2/DropdownMenu/DropdownMenu";
import CancelConfirmationModal from "@/components_v2/Modal/CancelConfirmationModal";
import { useSurveyViewPermissions } from "@/domains/survey-views/hooks/useSurveyViewPermissions";
import {
  selectCurrentUser,
  selectIsAuthenticated,
} from "@/redux/authentication/selectors";
import { SurveyViewPublicationDestination } from "@/redux/survey-views/types";
import BaseTag from "@/components_v2/Tags/BaseTag";
import ExternalLinkIcon from "@/assets/icons/external-link.svg?react";
import {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";

import "./styles.css";

const SurveyView = () => {
  const { surveyViewUuid } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notification } = App.useApp();

  const [zoomLevel, setZoomLevel] = useState(100);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { downloadSurveyView, isDownloading } = useSurveyViewDownload();
  const { data: previewData, isLoading: isPreviewDataLoading } =
    useViewSurveyViewQuery(surveyViewUuid as string);
  const { data: surveyViewData, isError: isSurveyViewError } =
    useGetSurveyViewByIdQuery(surveyViewUuid as string);
  const currentUser = useSelector(selectCurrentUser);
  const [getSurvey, surveyResponse] = useLazyGetSurveyByIdQuery();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteSurveyView] = useDeleteSurveyViewMutation();
  const [incrementSurveyViewViews] = useLazyIncrementSurveyViewViewsQuery();
  const { canEdit, canDelete } = useSurveyViewPermissions(
    surveyViewData,
    currentUser,
  );
  const [isIframeLoading, setIsIframeLoading] = useState(false);

  useEffect(() => {
    if (surveyViewData) {
      getSurvey({ surveyId: surveyViewData.survey });

      if (isAuthenticated) {
        setTimeout(() => {
          incrementSurveyViewViews(surveyViewData.id);
        }, 15000);
      }
    }
    if (isSurveyViewError) {
      navigate("/reports/views");
    }
  }, [surveyViewData, isSurveyViewError]);

  const handleZoom = (direction: "in" | "out") => {
    setZoomLevel((prev) => {
      const newZoom = direction === "in" ? prev + 25 : prev - 25;
      return Math.max(50, Math.min(200, newZoom));
    });
  };

  const handleFullscreen = async () => {
    try {
      if (!isFullscreen) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error("Fullscreen error:", error);
    }
  };

  const handleDeleteSurveyView = async (surveyViewId: string) => {
    deleteSurveyView(surveyViewId)
      .unwrap()
      .then(() => {
        notification.error({
          message: t("surveyViews.labels.surveyViewDeleted"),
        });
      });
  };

  const PUBLICATION_DESTINATION_TAGS: Record<
    SurveyViewPublicationDestination,
    { className: string; labelKey: string } | undefined
  > = {
    [SurveyViewPublicationDestination.WEBSITE]: {
      className: "public",
      labelKey: "surveyViews.labels.website",
    },
    [SurveyViewPublicationDestination.IN_APP]: {
      className: "prme-community",
      labelKey: "surveyViews.labels.in_app",
    },
    [SurveyViewPublicationDestination.EXTERNAL]: {
      className: "custom",
      labelKey: "surveyViews.labels.custom",
    },
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const modifiedHtmlData = useMemo(() => {
    if (!previewData?.htmlData) return "";
    return previewData.htmlData.replace(
      "<html",
      `<html style="transform: scale(${zoomLevel / 100}); transform-origin: top left; display: flex; -webkit-transform-origin-x: left;"`,
    );
  }, [previewData?.htmlData, zoomLevel]);

  const copyToClipboard = (surveyViewId: string) => {
    navigator.clipboard
      .writeText(
        `${window.location.origin}/public/reports/views/${surveyViewId}`,
      )
      .then(() => {
        notification.success({ message: t("shareLinkCopied") });
      });
  };

  const handleLinkedInSharing = () => {
    window.open(
      `https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}`,
      "_blank",
    );
  };

  return (
    <>
      {surveyViewData && (
        <Helmet>
          <title>{`PRME Commons - ${surveyViewData.title}`}</title>
          <meta
            name="og:title"
            content={`PRME Commons - ${surveyViewData.title}`}
          />
          <meta name="og:url" content={window.location.href} />
          <meta name="og:article" content="article" />
          <meta
            name="og:description"
            content={_.truncate(surveyViewData.description)}
          />
        </Helmet>
      )}
      <div className="flex justify-center full-width">
        <div className="survey-view-container-content flex flex-col gap-400 full-width">
          {isAuthenticated && (
            <BaseButton
              variant={BUTTON_VARIANTS.TERTIARY}
              size={BUTTON_SIZES.SMALL}
              type={BUTTON_TYPES.LINK}
              to={-1}
            >
              {t("datablocks.labels.back")}
            </BaseButton>
          )}

          <div className="survey-view-header flex gap-200 items-center self-center justify-between full-width">
            <h1 className="header-l-medium">
              {t("surveyViews.labels.downloadAndShare")}
            </h1>

            <div className="flex items-center gap-200">
              <DropdownMenu
                placement="bottom"
                content={
                  <div>
                    {(canEdit || canDelete) && (
                      <>
                        <h6 className="dropdown-menu-title body-s-medium">
                          {t("surveyViews.labels.actions")}
                        </h6>

                        {canEdit && (
                          <BaseButton
                            isFullWidth
                            className="dropdown-menu-button"
                            variant={BUTTON_VARIANTS.LINK}
                            leadingIcon={<EditOutlined />}
                            type={BUTTON_TYPES.LINK}
                            to={`/reports/views/${surveyViewUuid}/form`}
                          >
                            {t("buttons.editSurveyView")}
                          </BaseButton>
                        )}

                        {canDelete && (
                          <BaseButton
                            isFullWidth
                            className="dropdown-menu-button"
                            variant={BUTTON_VARIANTS.LINK}
                            leadingIcon={<DeleteOutlined />}
                            onClick={() => {
                              setOpenDeleteModal(true);
                            }}
                          >
                            {t("buttons.deleteSurveyView")}
                          </BaseButton>
                        )}
                        <BaseDivider />
                      </>
                    )}

                    <h6 className="dropdown-menu-title body-s-medium">
                      {t("surveyViews.labels.shareOptions")}
                    </h6>
                    {surveyViewUuid && (
                      <BaseButton
                        isFullWidth
                        className="dropdown-menu-button"
                        variant={BUTTON_VARIANTS.LINK}
                        leadingIcon={<LinkOutlined />}
                        onClick={() => copyToClipboard(surveyViewUuid)}
                      >
                        {t("surveyViews.labels.copyLink")}
                      </BaseButton>
                    )}

                    <BaseButton
                      isFullWidth
                      className="dropdown-menu-button"
                      variant={BUTTON_VARIANTS.LINK}
                      leadingIcon={<LinkedinOutlined />}
                      onClick={() => handleLinkedInSharing()}
                    >
                      {t("surveyViews.labels.shareOnLinkedIn")}
                    </BaseButton>
                  </div>
                }
              >
                <BaseButton variant={BUTTON_VARIANTS.SECONDARY}>•••</BaseButton>
              </DropdownMenu>

              {surveyViewUuid && surveyViewData ? (
                <BaseButton
                  leadingIcon={
                    isDownloading(surveyViewUuid) ? (
                      <BaseSpinner size="small" />
                    ) : (
                      <DownloadOutlined />
                    )
                  }
                  onClick={() =>
                    downloadSurveyView(
                      surveyViewUuid,
                      `${surveyViewData.title}.pdf`,
                    )
                  }
                  disabled={isDownloading(surveyViewUuid)}
                >
                  {isDownloading(surveyViewUuid)
                    ? t("surveyViews.labels.generating")
                    : t("surveyViews.labels.downloadAsPdf")}
                </BaseButton>
              ) : null}
            </div>
          </div>

          <div className="survey-view-main-content full-width gap-400">
            <div className="survey-view-info-panel">
              <div className="survey-view-info-section flex flex-col gap-300">
                <h2 className="survey-view-info-title header-m-medium">
                  {t("surveyViews.labels.surveyViewDetails")}
                </h2>
                {surveyResponse.isSuccess && (
                  <h2 className="survey-view-info-title header-s-medium">
                    {surveyResponse.data.title}
                  </h2>
                )}
                {surveyViewData && (
                  <>
                    <div className="survey-view-info-report-item-container flex gap-200">
                      <BaseSvgRenderer
                        component={SurveyIcon}
                        width="16"
                        height="16"
                      />
                      <h3 className="survey-view-info-report-title body-m-regular">
                        {surveyViewData.title}
                      </h3>
                    </div>

                    {surveyViewData.description && (
                      <div>
                        <div className="survey-view-info-description body-m-regular">
                          {surveyViewData.description}
                        </div>
                      </div>
                    )}

                    <BaseDivider />

                    <div className="survey-view-info-report-item-container flex gap-200">
                      <BaseSvgRenderer
                        component={CommunityIcon}
                        width="16"
                        height="16"
                      />
                      <Link
                        to={`/signatories/${surveyViewData.organization.id}`}
                      >
                        <div className="survey-view-info-organization body-m-regular">
                          {surveyViewData.organization?.name}
                        </div>
                      </Link>
                    </div>

                    <div className="survey-view-info-report-item-container flex gap-200">
                      <BaseSvgRenderer
                        component={CalendarIcon}
                        width="16"
                        height="16"
                      />
                      <div className="survey-view-info-date body-m-regular">
                        {DateTime.fromISO(surveyViewData.createdAt).toFormat(
                          "dd MMM yyyy",
                        )}
                      </div>
                    </div>

                    <div className="survey-view-info-report-item-container flex gap-200">
                      <BaseSvgRenderer
                        component={ExternalLinkIcon}
                        width="16"
                        height="16"
                      />
                      <div className="survey-view-info-date body-m-regular">
                        <BaseTag
                          size="small"
                          className={
                            PUBLICATION_DESTINATION_TAGS[
                              surveyViewData.config.publicationDestination
                            ]?.className || ""
                          }
                          label={t(
                            PUBLICATION_DESTINATION_TAGS[
                              surveyViewData.config.publicationDestination
                            ]?.labelKey || "",
                          )}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="survey-view-preview-section flex-1 full-width">
              <div className="survey-view-toolbar flex items-center justify-center">
                <div className="survey-view-zoom-controls flex items-center gap-200">
                  <BaseButton
                    size={BUTTON_SIZES.SMALL}
                    variant={BUTTON_VARIANTS.TERTIARY}
                    onClick={() => handleZoom("out")}
                    disabled={zoomLevel <= 50}
                  >
                    <ZoomOutOutlined />
                  </BaseButton>
                  <span className="survey-view-zoom-level">{zoomLevel}%</span>
                  <BaseButton
                    size={BUTTON_SIZES.SMALL}
                    variant={BUTTON_VARIANTS.TERTIARY}
                    onClick={() => handleZoom("in")}
                    disabled={zoomLevel >= 200}
                  >
                    <ZoomInOutlined />
                  </BaseButton>
                  <div className="survey-view-controls-separator" />
                  <BaseButton
                    size={BUTTON_SIZES.SMALL}
                    variant={BUTTON_VARIANTS.TERTIARY}
                    onClick={handleFullscreen}
                  >
                    {isFullscreen ? (
                      <FullscreenExitOutlined />
                    ) : (
                      <FullscreenOutlined />
                    )}
                  </BaseButton>
                </div>
              </div>

              <div
                className={`survey-view-container-content-preview flex full-width self-center justify-center ${isFullscreen ? "fullscreen" : ""}`}
              >
                {(isPreviewDataLoading || isIframeLoading) && (
                  <BaseSpinner className="survey-view-loading-spinner" />
                )}
                {previewData && (
                  <iframe
                    srcDoc={modifiedHtmlData}
                    title="Survey Data"
                    style={{
                      borderRadius: "var(--radius-s)",
                    }}
                    onLoad={() => setIsIframeLoading(false)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <CancelConfirmationModal
          title={t("surveyViews.labels.deleteSurveyView")}
          content={t("surveyViews.labels.deleteSurveyView")}
          confirmButtonText={t("buttons.delete")}
          open={openDeleteModal}
          onCancel={() => setOpenDeleteModal(false)}
          onConfirm={async () => {
            if (surveyViewUuid) {
              await handleDeleteSurveyView(surveyViewUuid);
            }
            setOpenDeleteModal(false);
            navigate("/reports/views");
          }}
        />
      </div>
    </>
  );
};

export default SurveyView;
